import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ModuleDataLoaderService } from '../module-data-loader.service';
import { LOCALE_CONFIGURATION, LOCALE_DATA } from './locale.domain';
import type { Locale, LocaleConfiguration, LocaleDataWithTranslations } from './locale.domain';

@Injectable({
  providedIn: 'root'
})
export class LocaleLoaderService extends ModuleDataLoaderService {
  private readonly translateService = inject<TranslateService>(TranslateService);
  private readonly localeConfiguration = inject<Array<LocaleConfiguration>>(LOCALE_CONFIGURATION, { optional: true });

  constructor() {
    super();
    const localeConfiguration = this.localeConfiguration;

    if (this.isConfigured()) {
      const locales: Array<string> | undefined = localeConfiguration?.map(
        (value: LocaleConfiguration): string => value.locale as string) || [];
      this.translateService.addLangs(locales);
      this.translateService.setFallbackLang(locales[0]);
    }
  }

  load(locale: Locale): Promise<LocaleDataWithTranslations> {
    if (this.isConfigured()) {
      const config = this.localeConfiguration?.find((value: LocaleConfiguration): boolean => value.locale === locale);
      if (config !== undefined) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return this.loadData(config, LOCALE_DATA)
          .then((result: LocaleDataWithTranslations): Promise<LocaleDataWithTranslations> => {
            this.translateService.setTranslation(locale, result.translations, true);
            return firstValueFrom(this.translateService.use(locale as string))
              .then((): LocaleDataWithTranslations => result);
          });
      }
    }
    return Promise.reject(new Error(`Locale "${locale}" is not configured.`));
  }

  private isConfigured(): boolean {
    return this.localeConfiguration !== null && this.localeConfiguration !== undefined && this.localeConfiguration.length > 0;
  }
}
